/**
 * O Financeiro além da baixa: lançamento avulso, transferência entre contas,
 * cadastro de contas e formas de pagamento, e conciliação por OFX.
 *
 * O leitor de OFX e a sugestão de vínculo ficam aqui, sem React — é regra que
 * precisa de teste com arquivo de verdade, e não de tela montada.
 */

import type { ContaFinanceira, Movimentacao, TipoDeTitulo, Titulo } from './tipos';

/* ------------------------------------------------------------- cadastros */

export type TipoDeMetodo = 'dinheiro' | 'pix' | 'debito' | 'credito' | 'boleto' | 'transferencia' | 'link';

export const TIPOS_DE_METODO: { valor: TipoDeMetodo; rotulo: string }[] = [
    { valor: 'pix', rotulo: 'Pix' },
    { valor: 'dinheiro', rotulo: 'Dinheiro' },
    { valor: 'debito', rotulo: 'Cartão de débito' },
    { valor: 'credito', rotulo: 'Cartão de crédito' },
    { valor: 'boleto', rotulo: 'Boleto' },
    { valor: 'transferencia', rotulo: 'Transferência' },
    { valor: 'link', rotulo: 'Link de pagamento' },
];

export const TIPOS_DE_CONTA: { valor: ContaFinanceira['tipo']; rotulo: string }[] = [
    { valor: 'banco', rotulo: 'Conta bancária' },
    { valor: 'caixa', rotulo: 'Caixa' },
    { valor: 'gateway', rotulo: 'Recebíveis de gateway' },
    { valor: 'aplicacao', rotulo: 'Aplicação' },
];

/** A conta como o cadastro a edita — com o saldo INICIAL, não o calculado. */
export interface ContaNoCadastro {
    id: string;
    nome: string;
    tipo: ContaFinanceira['tipo'];
    banco?: string;
    agencia?: string;
    numero?: string;
    saldoInicialCentavos: number;
    ativa: boolean;
}

export interface DadosDaConta {
    nome: string;
    tipo: ContaFinanceira['tipo'];
    banco?: string;
    agencia?: string;
    numero?: string;
    saldoInicialCentavos: number;
    ativa: boolean;
}

export interface MetodoNoCadastro {
    id: string;
    nome: string;
    tipo: TipoDeMetodo;
    taxaPercentual: number;
    prazoDias: number;
    contaId?: string;
    ativo: boolean;
}

export interface DadosDoMetodo {
    nome: string;
    tipo: TipoDeMetodo;
    taxaPercentual: number;
    prazoDias: number;
    contaId?: string;
    ativo: boolean;
}

export interface CadastrosFinanceiros {
    contas: ContaNoCadastro[];
    metodos: MetodoNoCadastro[];
}

/* --------------------------------------------------------------- títulos */

export interface NovoTitulo {
    tipo: TipoDeTitulo;
    descricao: string;
    /** Paciente, no a receber. */
    contatoId?: string;
    /** Fornecedor, no a pagar — texto, como o título já guardava. */
    fornecedor?: string;
    categoriaId?: string;
    valorCentavos: number;
    /** `YYYY-MM-DD`. */
    vencimento: string;
    /** `YYYY-MM`. Ausente: o mês do vencimento. */
    competencia?: string;
    observacoes?: string;
}

export type EdicaoDeTitulo = Omit<NovoTitulo, 'tipo'>;

export interface DetalheDoTitulo {
    titulo: Titulo;
    categoriaId?: string;
    fornecedor?: string;
    observacoes?: string;
    pagamentos: Movimentacao[];
    /** Baixas desfeitas, da mais recente para a mais antiga. */
    estornos?: PagamentoEstornado[];
}

export interface PagamentoEstornado {
    id: string;
    valorCentavos: number;
    /** `YYYY-MM-DD` do pagamento desfeito. */
    pagoEm: string;
    motivo?: string;
    /** ISO do estorno. */
    estornadoEm: string;
}

/** Competência `YYYY-MM` a partir de um dia. */
export function competenciaDoDia(dia: string): string {
    return dia.slice(0, 7);
}

/**
 * O primeiro dia do mês seguinte a `YYYY-MM`, para filtrar o mês por `< esse dia`.
 *
 * `<= YYYY-MM-31` parece o fim do mês e não é: setembro não tem dia 31, e o
 * Postgres recusa a data inteira ("date/time field value out of range") — a tela
 * do Financeiro caía assim em todo mês de 30 dias e em fevereiro.
 */
export function primeiroDiaDoMesSeguinte(mes: string): string {
    const [ano, m] = mes.split('-').map(Number);
    if (!ano || !m) throw new Error(`Mês inválido: ${mes}`);
    return m === 12 ? `${String(ano + 1)}-01-01` : `${String(ano)}-${String(m + 1).padStart(2, '0')}-01`;
}

/**
 * O vencimento da parcela `n` (1, 2, 3…) a partir do primeiro, mês a mês.
 *
 * O dia que não existe no mês cai no último dia dele — 31/01 vira 28/02 —, como
 * o `date + interval 'n months'` do Postgres que gera as parcelas no servidor
 * (0175). A prévia da tela e o banco precisam dar a mesma data.
 */
export function vencimentoDaParcela(primeiro: string, n: number): string {
    const [ano, mes, dia] = primeiro.split('-').map(Number);
    if (!ano || !mes || !dia) throw new Error(`Data inválida: ${primeiro}`);
    const alvo = new Date(Date.UTC(ano, mes - 1 + (n - 1), 1));
    const ultimo = new Date(Date.UTC(alvo.getUTCFullYear(), alvo.getUTCMonth() + 1, 0)).getUTCDate();
    alvo.setUTCDate(Math.min(dia, ultimo));
    return alvo.toISOString().slice(0, 10);
}

/**
 * O valor de cada parcela: a divisão inteira, com o centavo que sobra na
 * primeira — a mesma regra do servidor (0175).
 */
export function valoresDasParcelas(totalCentavos: number, parcelas: number): number[] {
    const base = Math.floor(totalCentavos / parcelas);
    const resto = totalCentavos - base * parcelas;
    return Array.from({ length: parcelas }, (_, i) => base + (i === 0 ? resto : 0));
}

/* ------------------------------------------------------- transferências */

export interface Transferencia {
    id: string;
    origemId: string;
    origem: string;
    destinoId: string;
    destino: string;
    valorCentavos: number;
    /** `YYYY-MM-DD`. */
    data: string;
    observacao?: string;
}

export interface NovaTransferencia {
    origemId: string;
    destinoId: string;
    valorCentavos: number;
    data: string;
    observacao?: string;
}

/** O efeito das transferências no saldo de cada conta: entrou menos saiu. */
export function efeitoDasTransferencias(transferencias: Transferencia[], ateDia: string): Map<string, number> {
    const efeito = new Map<string, number>();
    for (const t of transferencias) {
        if (t.data > ateDia) continue;
        efeito.set(t.origemId, (efeito.get(t.origemId) ?? 0) - t.valorCentavos);
        efeito.set(t.destinoId, (efeito.get(t.destinoId) ?? 0) + t.valorCentavos);
    }
    return efeito;
}

/* ----------------------------------------------------------- conciliação */

/** Uma transação lida do arquivo, antes de ir para o banco. */
export interface LinhaDoOfx {
    fitid: string;
    /** `YYYY-MM-DD`. */
    data: string;
    /** Com sinal: positivo entrou, negativo saiu. */
    valorCentavos: number;
    descricao: string;
}

export type SituacaoDaLinha = 'pendente' | 'conciliada' | 'ignorada';

export interface LinhaDoExtratoImportada extends LinhaDoOfx {
    id: string;
    contaId: string;
    situacao: SituacaoDaLinha;
    tituloId?: string;
    tituloDescricao?: string;
}

function valorDaTag(bloco: string, tag: string): string | undefined {
    const casa = new RegExp(`<${tag}>\\s*([^<\\r\\n]*)`, 'i').exec(bloco);
    const valor = casa?.[1]?.trim();
    return valor ? valor : undefined;
}

/** "1500.00", "-72,50" ou "1.234,56" viram centavos com sinal. */
function centavosDoOfx(bruto: string): number | null {
    let texto = bruto.replace(/\s/g, '');
    if (texto.includes(',') && texto.includes('.')) texto = texto.replace(/\./g, '').replace(',', '.');
    else if (texto.includes(',')) texto = texto.replace(',', '.');
    const n = Number(texto);
    return Number.isFinite(n) ? Math.round(n * 100) : null;
}

/**
 * Lê as transações de um arquivo OFX (SGML ou XML).
 *
 * Só o que a conciliação usa: data, valor com sinal, descrição e o `FITID`.
 * Transação sem `FITID` ganha um identificador feito de data, valor e ordem —
 * importa, mas a garantia de não duplicar fica mais fraca, e a tela diz isso.
 */
export function lerOfx(texto: string): { linhas: LinhaDoOfx[]; semFitid: number; descartadas: number } {
    const blocos = texto.split(/<STMTTRN>/i).slice(1).map((b) => b.split(/<\/STMTTRN>/i)[0] ?? b);
    const linhas: LinhaDoOfx[] = [];
    const repeticoes = new Map<string, number>();
    let semFitid = 0;
    let descartadas = 0;

    for (const bloco of blocos) {
        const quando = valorDaTag(bloco, 'DTPOSTED');
        const bruto = valorDaTag(bloco, 'TRNAMT');
        const valor = bruto ? centavosDoOfx(bruto) : null;
        if (!quando || !/^\d{8}/.test(quando) || valor === null || valor === 0) {
            descartadas += 1;
            continue;
        }
        const data = `${quando.slice(0, 4)}-${quando.slice(4, 6)}-${quando.slice(6, 8)}`;
        const descricao = valorDaTag(bloco, 'MEMO') ?? valorDaTag(bloco, 'NAME') ?? '';
        let fitid = valorDaTag(bloco, 'FITID');
        if (!fitid) {
            semFitid += 1;
            const chave = `${data}:${valor}`;
            const ordem = (repeticoes.get(chave) ?? 0) + 1;
            repeticoes.set(chave, ordem);
            fitid = `sem-fitid-${data}-${valor}-${ordem}`;
        }
        linhas.push({ fitid, data, valorCentavos: valor, descricao });
    }
    return { linhas, semFitid, descartadas };
}

function diasEntreDias(a: string, b: string): number {
    return Math.abs(Math.round((Date.parse(`${a}T12:00:00Z`) - Date.parse(`${b}T12:00:00Z`)) / 86_400_000));
}

/**
 * Os títulos que PODEM ser esta linha do extrato.
 *
 * Mesmo sentido (entrada com a receber, saída com a pagar), em aberto, com o
 * valor exato do que falta, e vencimento a no máximo `janelaDias` da data. A
 * sugestão é só isso — quem confirma o vínculo é uma pessoa.
 */
export function sugerirTitulos(linha: LinhaDoOfx, titulos: Titulo[], janelaDias = 7): Titulo[] {
    const tipo: TipoDeTitulo = linha.valorCentavos > 0 ? 'receber' : 'pagar';
    const alvo = Math.abs(linha.valorCentavos);
    return titulos
        .filter((t) => t.tipo === tipo
            && (t.situacao === 'aberto' || t.situacao === 'parcial')
            && t.valorCentavos - t.pagoCentavos === alvo
            && diasEntreDias(t.vencimento, linha.data) <= janelaDias)
        .sort((a, b) => diasEntreDias(a.vencimento, linha.data) - diasEntreDias(b.vencimento, linha.data))
        .slice(0, 3);
}
